import {
  ApInvoiceTransactionStatusEnum,
  ComponentTypeForPost,
  useCreateTransactionItem,
} from '@/context/services/transaction_items/List/TransactionItems.service';
import { TransactionItemFormType } from '@/features/transaction_items/List/type';
import { NUMERIC_TYPE_SUPPORTED_DECIMAL_PLACES } from '@/features/transaction_items/constants';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useCallback } from 'react';
import { SubmitHandler, UseFormSetError } from 'react-hook-form';
import { useParams } from 'react-router-dom';

type Props = {
  setValidationError: UseFormSetError<TransactionItemFormType>;
  handlerSuccess: (successMsg: string) => Promise<void>;
  handlerError: (errorMsg: string) => void;
  showLoading: (loading: boolean) => void;
};

const toOptionalString = (
  value: string | number | null | undefined
): string | undefined => {
  if (value === null || value === undefined) return undefined;
  return value.toString();
};

export const useSubmitCreateTransactionItem = ({
  setValidationError,
  handlerSuccess,
  handlerError,
  showLoading,
}: Props) => {
  const { report_type_id } = useParams();
  const { mutateAsync } = useCreateTransactionItem(report_type_id ?? '');
  const { send } = useErrorTrackingSystem();
  const { t } = useTranslation();

  const onSubmitCreate: SubmitHandler<TransactionItemFormType> = useCallback(
    async (form) => {
      try {
        showLoading(true);
        const resp = await mutateAsync({
          reportTypeId: report_type_id ?? '',
          data: {
            ap_invoice_transaction_general_input: {
              label: form.label,
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              component_type: form.component_type as ComponentTypeForPost,
              is_visible: form.is_visible ?? false,
              date_format_type: form.date_format_type,
              is_required: false,
              min_value: toOptionalString(form.min_value),
              max_value: toOptionalString(form.max_value),
              min_chars: toOptionalString(form.min_chars),
              max_chars: toOptionalString(form.max_chars),
              decimal_places: form.allow_decimal
                ? NUMERIC_TYPE_SUPPORTED_DECIMAL_PLACES
                : 0,
              placeholder: form.placeholder,
              tooltip_message: form.tooltip_message,
            },
          },
        });
        if (resp.status === 'success') {
          await handlerSuccess(t('transaction_items.create.success'));
        } else if (
          resp.status === ApInvoiceTransactionStatusEnum.INVALID_ERROR
        ) {
          const errorTypeMap: Record<string, string> = {
            taken: t('transaction_items.validation_message.taken'),
            blank: t('transaction_items.validation_message.required'),
          };

          Object.entries(resp.errors.errors).forEach(([key, value]) => {
            if (key in form) {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              const keyType = key as keyof TransactionItemFormType;
              const errorMessage = errorTypeMap[value.type] ?? value.message;

              setValidationError(keyType, {
                type: 'validate',
                message: errorMessage,
              });
            } else {
              handlerError(t('transaction_items.create.error'));
            }
          });
        } else if (resp.status === ApInvoiceTransactionStatusEnum.AUTH_ERROR) {
          location.href = resp.redirectPath;
        } else {
          handlerError(t('transaction_items.create.error'));
        }
      } catch (error) {
        if (error instanceof Error && error?.name === 'AxiosError') {
          send(error.message);
        } else {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          send(error as Error);
        }
        handlerError(t('transaction_items.create.error'));
      } finally {
        showLoading(false);
      }
    },
    [
      showLoading,
      mutateAsync,
      report_type_id,
      handlerSuccess,
      t,
      setValidationError,
      handlerError,
      send,
    ]
  );

  return { onSubmitCreate };
};
